import * as Switch from "@radix-ui/react-switch";
import { useId } from "react";

import { searchMunicipalities } from "../../api/me";
import type { Municipality, Profile, Transport } from "../../api/me";
import { Combobox } from "../../components/Combobox";
import { nl } from "../../i18n/nl";
import styles from "./prefs.module.css";

const t = nl.prefs;

export const PROFILES: readonly Profile[] = ["netwerk", "systemen", "security", "cloud", "helpdesk", "onbekend"];
const TRANSPORTS: readonly Transport[] = ["auto", "ov", "fiets"];
const DISTANCES = [10, 25, 50, 100] as const;
export const NOTIFY_KEYS = ["claim", "wait", "follow", "digest", "email"] as const;
export type NotifyKey = (typeof NOTIFY_KEYS)[number];

export function ProfilePicker({
  value,
  onChange,
  error,
}: {
  value: Profile[];
  onChange: (next: Profile[]) => void;
  error?: string | null;
}) {
  const id = useId();
  const errorId = `${id}-error`;
  return (
    <fieldset className={styles.fieldset} aria-describedby={error ? errorId : undefined}>
      <legend className={styles.legend}>{t.profilesLabel}</legend>
      <p className={styles.help}>{t.profilesHelp}</p>
      <div className={styles.options}>
        {PROFILES.map((profile) => {
          const checked = value.includes(profile);
          return (
            <label
              key={profile}
              htmlFor={`${id}-${profile}`}
              className={[styles.option, checked && styles.optionOn].filter(Boolean).join(" ")}
            >
              <input
                id={`${id}-${profile}`}
                type="checkbox"
                checked={checked}
                onChange={() => {
                  onChange(checked ? value.filter((p) => p !== profile) : [...value, profile]);
                }}
              />
              <span className={styles.optionLabel}>{t.profiles[profile].label}</span>
              <span className={styles.optionText}>{t.profiles[profile].text}</span>
            </label>
          );
        })}
      </div>
      {error && (
        <p id={errorId} className={styles.error} role="alert">
          {error}
        </p>
      )}
    </fieldset>
  );
}

/** Start municipality: a label from `GET /municipalities` (name or postcode). */
export function MunicipalityPicker({
  value,
  onChange,
  label = t.municipality,
  help = t.municipalityHelp,
  error,
}: {
  value: string | null;
  onChange: (label: string | null) => void;
  label?: string;
  help?: string;
  error?: string | null;
}) {
  return (
    <Combobox<Municipality>
      label={label}
      help={help}
      error={error}
      placeholder={t.municipalityPlaceholder}
      value={value}
      queryKey="municipalities"
      search={async (term) =>
        (await searchMunicipalities(term)).map((m) => ({
          key: m.label,
          primary: m.label,
          secondary: `${m.postcodes.join(", ")} · ${m.province}`,
          data: m,
        }))
      }
      onSelect={(option) => {
        onChange(option.data.label);
      }}
      onClear={() => {
        onChange(null);
      }}
      clearLabel={t.municipalityClear}
      noneText={t.municipalityNone}
      searchingText={t.municipalitySearching}
      footer={
        <p className={styles.credit}>
          <a href="https://www.geonames.org" target="_blank" rel="noreferrer">
            {t.municipalityCredit}
          </a>
        </p>
      }
    />
  );
}

function RadioRow<T extends string | number | null>({
  legend,
  options,
  value,
  onChange,
}: {
  legend: string;
  options: { value: T; label: string }[];
  value: T;
  onChange: (next: T) => void;
}) {
  const name = useId();
  return (
    <fieldset className={styles.fieldset}>
      <legend className={styles.legend}>{legend}</legend>
      <div className={styles.segments}>
        {options.map((option) => (
          <label
            key={String(option.value)}
            className={[styles.segment, value === option.value && styles.segmentOn].filter(Boolean).join(" ")}
          >
            <input
              type="radio"
              name={name}
              checked={value === option.value}
              onChange={() => {
                onChange(option.value);
              }}
            />
            {option.label}
          </label>
        ))}
      </div>
    </fieldset>
  );
}

export function DistancePicker({ value, onChange }: { value: number | null; onChange: (km: number | null) => void }) {
  const options: { value: number | null; label: string }[] = [
    ...DISTANCES.map((km) => ({ value: km, label: t.distanceOptions[km] })),
    { value: null, label: t.distanceAny },
  ];
  return <RadioRow legend={t.distance} options={options} value={value} onChange={onChange} />;
}

export function TransportPicker({
  value,
  onChange,
}: {
  value: Transport | null;
  onChange: (transport: Transport | null) => void;
}) {
  return (
    <RadioRow<Transport | null>
      legend={t.transport}
      options={TRANSPORTS.map((transport) => ({ value: transport, label: t.transports[transport] }))}
      value={value}
      onChange={onChange}
    />
  );
}

export function NotifySwitches({
  value,
  onChange,
  legend = t.notifyLabel,
}: {
  value: Record<NotifyKey, boolean>;
  onChange: (next: Record<NotifyKey, boolean>) => void;
  legend?: string;
}) {
  const id = useId();
  return (
    <fieldset className={styles.fieldset}>
      <legend className={styles.legend}>{legend}</legend>
      <p className={styles.help}>{t.notifyHelp}</p>
      <div className={styles.switches}>
        {NOTIFY_KEYS.map((key) => (
          <div key={key} className={styles.switchRow}>
            <label htmlFor={`${id}-${key}`} className={styles.switchLabel}>
              <span className={styles.optionLabel}>{t.notify[key].label}</span>
              <span className={styles.optionText}>{t.notify[key].text}</span>
            </label>
            <Switch.Root
              id={`${id}-${key}`}
              className={styles.switch}
              checked={value[key]}
              onCheckedChange={(checked) => {
                onChange({ ...value, [key]: checked });
              }}
            >
              <Switch.Thumb className={styles.thumb} />
            </Switch.Root>
          </div>
        ))}
      </div>
    </fieldset>
  );
}
